/**
 * Plain-language replacement for Uppy's `youCanOnlyUploadFileTypes` restriction error.
 *
 * Uppy's built-in string is `You can only upload: %{types}` and fills `%{types}` with
 * `restrictions.allowedFileTypes.join(', ')`, so the main upload window showed ~30 raw MIME
 * types (`application/vnd.openxmlformats-officedocument...`) in the error toast (#2551).
 * We instead name the kinds of file the window accepts. Callers narrow the list (Replace Asset
 * keeps the original asset's kind, a smallapp field sets its own), so the message is picked from
 * the categories actually present rather than hardcoded to "image, video, audio or document".
 */
import type { TFunction } from 'i18next';
import { VIDEO_MIME_TYPES } from '@scio3/shared-types';

const FILE_CATEGORY = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  DOCUMENT: 'document',
  OTHER: 'other',
} as const;

type FileCategory = (typeof FILE_CATEGORY)[keyof typeof FILE_CATEGORY];

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp']);
const VIDEO_EXTENSIONS = new Set([
  '.mp4',
  '.3gp',
  '.m4v',
  '.webm',
  '.avi',
  '.mov',
  '.mpeg',
  '.wmv',
]);
const AUDIO_EXTENSIONS = new Set(['.mp3']);
const DOCUMENT_EXTENSIONS = new Set([
  '.pdf',
  '.doc',
  '.docx',
  '.ppt',
  '.pptx',
  '.xls',
  '.xlsx',
  '.odt',
  '.ods',
]);

// HLS/DASH manifests are `application/*` but are video assets (backend VIDEO_TYPES).
const VIDEO_APPLICATION_TYPES = new Set(
  VIDEO_MIME_TYPES.filter((type) => type.startsWith('application/')).map((type) =>
    type.toLowerCase(),
  ),
);

function getFileCategory(allowedType: string): FileCategory {
  const type = allowedType.trim().toLowerCase();
  if (type.startsWith('.')) {
    if (IMAGE_EXTENSIONS.has(type)) return FILE_CATEGORY.IMAGE;
    if (VIDEO_EXTENSIONS.has(type)) return FILE_CATEGORY.VIDEO;
    if (AUDIO_EXTENSIONS.has(type)) return FILE_CATEGORY.AUDIO;
    if (DOCUMENT_EXTENSIONS.has(type)) return FILE_CATEGORY.DOCUMENT;
    return FILE_CATEGORY.OTHER;
  }
  if (type.startsWith('image/')) return FILE_CATEGORY.IMAGE;
  if (type.startsWith('video/') || VIDEO_APPLICATION_TYPES.has(type)) return FILE_CATEGORY.VIDEO;
  if (type.startsWith('audio/')) return FILE_CATEGORY.AUDIO;
  if (type.startsWith('application/')) return FILE_CATEGORY.DOCUMENT;
  return FILE_CATEGORY.OTHER;
}

export function getUnsupportedFileTypeMessage(
  allowedFileTypes: readonly string[] | undefined,
  t: TFunction,
): string {
  const categories = new Set((allowedFileTypes ?? []).map(getFileCategory));

  if (categories.size === 1) {
    if (categories.has(FILE_CATEGORY.IMAGE))
      return t("This file type isn't supported. Please upload a JPG, PNG, GIF, BMP, or SVG.");
    if (categories.has(FILE_CATEGORY.VIDEO))
      return t(
        "This file type isn't supported. Please upload an MP4, MOV, M4V, WebM, AVI, WMV, MPEG, or 3GP.",
      );
    if (categories.has(FILE_CATEGORY.AUDIO))
      return t("This file type isn't supported. Please upload an MP3.");
    if (categories.has(FILE_CATEGORY.DOCUMENT))
      return t(
        "This file type isn't supported. Please upload a PDF, PowerPoint, Word, Excel, or OpenDocument file.",
      );
  }

  if (
    categories.has(FILE_CATEGORY.IMAGE) &&
    categories.has(FILE_CATEGORY.VIDEO) &&
    categories.has(FILE_CATEGORY.AUDIO) &&
    categories.has(FILE_CATEGORY.DOCUMENT)
  ) {
    return t(
      "This file type isn't supported. Please upload JPG, PNG, MP4, MOV, PDF, PowerPoint, or Word files.",
    );
  }

  return t("This file type isn't supported.");
}

/** The upload window's note when only one kind of file is allowed (Replace File keeps the asset's
 *  kind), e.g. "Images only"; null otherwise. The exact formats show in the refusal banner. */
export function getAllowedFileTypesNote(
  allowedFileTypes: readonly string[] | undefined,
  t: TFunction,
): string | null {
  const categories = new Set((allowedFileTypes ?? []).map(getFileCategory));
  if (categories.size !== 1) return null;
  if (categories.has(FILE_CATEGORY.IMAGE)) return t('Images only');
  if (categories.has(FILE_CATEGORY.VIDEO)) return t('Videos only');
  if (categories.has(FILE_CATEGORY.AUDIO)) return t('Audio only');
  if (categories.has(FILE_CATEGORY.DOCUMENT)) return t('Documents only');
  return null;
}
